// src/components/MasjidAzanCard.js
import { useEffect, useMemo, useState } from "react";
import usePrayerTimes from "../utils/usePrayerTimes";
import { prayerNames, formatTime } from "../utils/prayerUtils";
import "../styles/MasjidAzanCard.css";

const EMPTY_OBJ = Object.freeze({});
const NORM = (s = "") => String(s || "").trim().toLowerCase();

const FARADHU = ["fajr", "dhuhr", "asr", "maghrib", "isha"];

const SETTING_KEY = {
    fajr: "Subuh",
    dhuhr: "Zohor",
    asr: "Asar",
    maghrib: "Maghrib",
    isha: "Isyak",
};

// ===== time helpers (robust) =====
const normalizeTime = (input) => {
    if (!input) return "";
    return String(input)
        .replace(/\u202F/g, " ")
        .replace(/\u00A0/g, " ")
        .trim();
};

const parseToMinutes = (t) => {
    if (!t) return null;
    const s0 = normalizeTime(t);

    // detect AM/PM
    const apm = s0.match(/\b(am|pm)\b/i);
    const ap = apm ? apm[1].toUpperCase() : null;

    // ambil H & MM sahaja (ignore seconds/text)
    const m = s0.match(/(\d{1,2})\D+(\d{2})/);
    if (!m) return null;

    let hh = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;

    if (ap) {
        if (ap === "AM") {
            if (hh === 12) hh = 0;
        } else {
            if (hh !== 12) hh += 12;
        }
    }

    return (hh % 24) * 60 + mm;
};

const minutesToHHMM = (mins) => {
    const total = ((mins % (24 * 60)) + (24 * 60)) % (24 * 60);
    const hh = Math.floor(total / 60);
    const mm = total % 60;
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

const valueToHHMM = (v) => {
    if (!v) return null;

    // kalau hook bagi string "HH:MM" / "7:14 PM" → parse terus
    if (typeof v === "string") {
        const mins = parseToMinutes(v);
        return mins == null ? null : minutesToHHMM(mins);
    }

    // kalau Date/Timestamp-ish → guna formatTime dulu
    try {
        const s = normalizeTime(formatTime(v));
        const mins = parseToMinutes(s);
        return mins == null ? null : minutesToHHMM(mins);
    } catch {
        return null;
    }
};

const msTodayAtHHMM = (hhmm) => {
    const mins = parseToMinutes(hhmm);
    if (mins == null) return null;
    const d = new Date();
    d.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
    return d.getTime();
};

const msToHMS = (ms) => {
    const s = Math.max(0, Math.ceil(ms / 1000));
    const hh = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
};

const addMinutesHHMM = (hhmm, addMin) => {
    const base = parseToMinutes(hhmm);
    if (base == null) return null;
    return minutesToHHMM(base + (Number(addMin) || 0));
};

const hhmm24To12 = (hhmm) => {
    if (!hhmm) return "--:--";
    const mins = parseToMinutes(hhmm);
    if (mins == null) return "--:--";
    let hh = Math.floor(mins / 60);
    const mm = mins % 60;
    const ap = hh >= 12 ? "PM" : "AM";
    let h12 = hh % 12;
    if (h12 === 0) h12 = 12;
    return `${h12}:${String(mm).padStart(2, "0")} ${ap}`;
};


export default function MasjidAzanCard({ masjid }) {
    // assets
    const azanPng = `${process.env.PUBLIC_URL}/assets/azan.png`;
    const berjemaahPng = `${process.env.PUBLIC_URL}/assets/berjemaah.png`;
    const fallbackAvatar = `${process.env.PUBLIC_URL}/assets/imambilal.png`;

    // ETV config
    const etv = useMemo(() => masjid?.etv || masjid?.profail?.etv || EMPTY_OBJ, [masjid]);
    const zon = etv?.zon || "";
    const ps = useMemo(() => etv?.prayerSetting || EMPTY_OBJ, [etv]);

    // show only bila zon ada (aktif ETV)
    const canShow = useMemo(() => Boolean(NORM(zon)), [zon]);

    // hook mesti jalan walaupun zon kosong
    const zoneCode = zon || "WLY01";
    const { prayerTimes } = usePrayerTimes(zoneCode);

    // now tick
    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        const t = window.setInterval(() => setNowMs(Date.now()), 1000);
        return () => window.clearInterval(t);
    }, []);

    // build schedule fardhu (ikut data prayerTimes yang kau dah confirm boleh)
    const schedule = useMemo(() => {
        if (!prayerTimes) return [];
        const list = FARADHU.map((key) => {
            const hhmm = valueToHHMM(prayerTimes?.[key]);
            const ms = hhmm ? msTodayAtHHMM(hhmm) : null;
            return { key, hhmm, ms };
        }).filter((x) => x.hhmm && typeof x.ms === "number");
        list.sort((a, b) => a.ms - b.ms);
        return list;
    }, [prayerTimes]);

    // derive active prayer (yang baru masuk) & next prayer
    const derived = useMemo(() => {
        if (!canShow) return { status: "HIDE" };
        if (!schedule.length) return { status: "LOADING" };

        const dayMs = 24 * 60 * 60 * 1000;

        // next = first after now, else tomorrow first
        let next = schedule.find((x) => nowMs < x.ms);
        if (!next) {
            const first = schedule[0];
            next = { ...first, ms: first.ms + dayMs };
        }

        // active = last <= now, else yesterday last
        let active = null;
        for (let i = schedule.length - 1; i >= 0; i--) {
            if (nowMs >= schedule[i].ms) {
                active = schedule[i];
                break;
            }
        }
        if (!active) {
            const last = schedule[schedule.length - 1];
            active = { ...last, ms: last.ms - dayMs };
        }

        // settings ikut ACTIVE (solat yang baru masuk)
        const label = SETTING_KEY[active.key];
        const iqamahMins = Number(ps?.[label]?.masaIqamah) || 0;
        const solatMins = Number(ps?.[label]?.skrinGelap) || 0;

        const azanMs = active.ms;
        const iqamahMs = azanMs + iqamahMins * 60 * 1000;
        const endMs = iqamahMs + solatMins * 60 * 1000;

        const iqamahHHMM = active.hhmm ? addMinutesHHMM(active.hhmm, iqamahMins) : null;

        // phase ikut jam sebenar (refresh pun betul)
        if (nowMs >= azanMs && nowMs < iqamahMs) {
            return {
                status: "OK",
                phase: "IQAMAH",
                activeKey: active.key,
                nextKey: next.key,
                nextHHMM: next.hhmm,
                iqamahHHMM,
                iqamahMins,
                solatMins,
                phaseMsLeft: iqamahMs - nowMs,
            };
        }

        if (nowMs >= iqamahMs && nowMs < endMs && solatMins > 0) {
            return {
                status: "OK",
                phase: "SOLAT",
                activeKey: active.key,
                nextKey: next.key,
                nextHHMM: next.hhmm,
                iqamahHHMM,
                iqamahMins,
                solatMins,
                phaseMsLeft: endMs - nowMs,
            };
        }

        // default: AZAN (countdown to next)
        return {
            status: "OK",
            phase: "AZAN",
            activeKey: active.key,
            nextKey: next.key,
            nextHHMM: next.hhmm,
            iqamahHHMM,
            iqamahMins,
            solatMins,
            phaseMsLeft: Math.max(0, next.ms - nowMs),
        };
    }, [canShow, schedule, nowMs, ps]);

    // ✅ jangan return awal sebelum hooks
    if (!canShow) return null;

    const bilalName =
        masjid?.profail?.bilalBertugas || masjid?.bilalBertugas || "Bilal bertugas";
    const imamName =
        masjid?.profail?.imamBertugas || masjid?.imamBertugas || "Imam bertugas";

    const imamImg =
        masjid?.profail?.imamBertugasGambar || masjid?.imamBertugasGambar || "";
    const bilalImg =
        masjid?.profail?.bilalBertugasGambar || masjid?.bilalBertugasGambar || "";

    if (derived.status === "LOADING") {
        return (
            <div className="azan-card">
                <div className="azan-card-top">
                    <div className="azan-title-row">
                        <img className="azan-title-icon" src={azanPng} alt="" />
                        <div className="azan-title">Memuat waktu solat...</div>
                    </div>
                    <div className="azan-meta">
                        <span className="azan-zon">{zon}</span>
                    </div>
                </div>
            </div>
        );
    }

    const currentName = derived.activeKey ? prayerNames[derived.activeKey]?.display : "Solat";
    const nextName = derived.nextKey ? prayerNames[derived.nextKey]?.display : "Solat";

    return (
        <div className="azan-card">
            <div className="azan-card-top">
                <div className="azan-title-row">
                    <img
                        className="azan-title-icon"
                        src={derived.phase === "SOLAT" ? berjemaahPng : azanPng}
                        alt=""
                    />
                    <div>
                        <div className="azan-title">
                            {derived.phase === "AZAN" && (
                                <>
                                    Bilal akan azan <b>{nextName}</b> pada:    <b>{hhmm24To12(derived.nextHHMM)}</b>

                                </>
                            )}

                            {derived.phase === "IQAMAH" && (
                                <>
                                    Telah masuk waktu <b>{currentName}</b>
                                </>
                            )}

                            {derived.phase === "SOLAT" && (
                                <>
                                    Solat <b>{currentName}</b> berjemaah sedang didirikan.
                                </>
                            )}
                        </div>
                        <div className="azan-card-mid">
                            {derived.phase === "AZAN" && (
                                <div className="azan-sub">
                                    {derived.phaseMsLeft ? `Dalam ${msToHMS(derived.phaseMsLeft)}` : ""}
                                </div>
                            )}

                            {derived.phase === "IQAMAH" && (
                                <>
                                    {/* <div className="azan-time">{hhmm24To12(derived.iqamahHHMM)}</div> */}

                                    <div className="azan-sub">
                                        {/* (Anggaran ikut setting masjid: {derived.iqamahMins} minit selepas azan) */}
                                        Iqamah dalam <b>{msToHMS(derived.phaseMsLeft)}</b>                                    </div>
                                </>
                            )}

                            {derived.phase === "SOLAT" && (
                                <div className="azan-sub">
                                    Anggaran selesai solat: <b>{msToHMS(derived.phaseMsLeft)}</b>{" "}
                                    {/* <span style={{ opacity: 0.75 }}>(etv gelap: {derived.solatMins} minit)</span> */}
                                </div>
                            )}
                        </div>
                    </div>

                </div>

                <div className="azan-meta">
                    <span className="azan-zon">{zon}</span>
                </div>
            </div>



            <div className="azan-card-bottom">
                <div className="azan-person">
                    <div className="azan-person-left">

                        <div className="azan-person-text">
                            <span className="label">Imam</span>
                            <span className="value">{imamName}</span>
                        </div>
                        <img
                            className="azan-avatar"
                            src={imamImg || fallbackAvatar}
                            alt="Imam"
                            onError={(e) => (e.currentTarget.src = fallbackAvatar)}
                        />
                    </div>
                </div>

                <div className="azan-person">
                    <div className="azan-person-left">

                        <div className="azan-person-text">
                            <span className="label">Bilal</span>
                            <span className="value">{bilalName}</span>
                        </div>
                        <img
                            className="azan-avatar"
                            src={bilalImg || fallbackAvatar}
                            alt="Bilal"
                            onError={(e) => (e.currentTarget.src = fallbackAvatar)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
